import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Female as FemaleIcon,
  TrendingDown,
  LocationCity,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { earlyWarningAPI } from '../../services/earlyWarningApi';

const riskColors: Record<string, string> = {
  critical: '#d32f2f',
  high: '#f44336',
  medium: '#ff9800',
  low: '#4caf50',
};

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ReactNode; color: string }> = ({
  label, value, icon, color,
}) => (
  <Paper sx={{ p: 2.5, textAlign: 'center', borderTop: `4px solid ${color}` }}>
    <Box sx={{ color, mb: 1 }}>{icon}</Box>
    <Typography variant="h3" fontWeight={700} color={color}>{value}</Typography>
    <Typography variant="body2" fontWeight={600}>{label}</Typography>
  </Paper>
);

const EarlyWarningDashboard: React.FC = () => {
  const [tab, setTab] = React.useState(0);
  const [selectedDistrict, setSelectedDistrict] = React.useState<string>('');

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['early-warning-summary'],
    queryFn: () => earlyWarningAPI.getSummary(),
    select: (res) => res.data,
  });

  const { data: schoolData, isLoading: loadingSchools } = useQuery({
    queryKey: ['early-warning-schools', selectedDistrict],
    queryFn: () => earlyWarningAPI.getSchools({ district: selectedDistrict || undefined }),
    select: (res) => res.data,
    enabled: tab === 2,
  });

  if (loadingSummary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) return <Alert severity="error">Failed to load early warning data.</Alert>;

  const transitionChartData = summary.districts?.map((d: any) => ({
    district: d.district,
    male: d.male_transition_rate,
    female: d.female_transition_rate,
    overall: d.transition_rate,
    zone: d.zone,
  })) || [];

  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight={700} mb={0.5}>
        At-Risk Early Warning Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Secondary transition risk analysis and gender-specific alerts
        {summary.academic_year && ` (Std VII ${summary.previous_year} → F1 ${summary.academic_year})`}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="National Transition Rate"
            value={`${summary.summary?.national_transition_rate || 0}%`}
            icon={<TrendingDown fontSize="large" />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Districts at Risk"
            value={summary.summary?.districts_at_risk || 0}
            icon={<LocationCity fontSize="large" />}
            color="#f44336"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Gender Gap Alerts"
            value={summary.summary?.gender_alert_count || 0}
            icon={<FemaleIcon fontSize="large" />}
            color="#e91e63"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Critical Districts"
            value={summary.summary?.critical_districts || 0}
            icon={<WarningIcon fontSize="large" />}
            color="#d32f2f"
          />
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="District Risk Overview" />
        <Tab label="Gender Alerts" />
        <Tab label="School Details" />
      </Tabs>

      {/* Tab 0: District Risk */}
      {tab === 0 && (
        <>
          {/* Transition Rate Chart */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Primary → Secondary Transition Rate by District & Gender
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={transitionChartData} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} unit="%" />
                <YAxis type="category" dataKey="district" width={110} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="male" name="Male" fill="#1976d2" barSize={10} />
                <Bar dataKey="female" name="Female" fill="#e91e63" barSize={10} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* Risk Score Table */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>District Risk Scores</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>District</strong></TableCell>
                    <TableCell><strong>Zone</strong></TableCell>
                    <TableCell align="right"><strong>Transition Rate</strong></TableCell>
                    <TableCell align="right"><strong>Male</strong></TableCell>
                    <TableCell align="right"><strong>Female</strong></TableCell>
                    <TableCell align="right"><strong>Gender Gap</strong></TableCell>
                    <TableCell align="right"><strong>Dropout Rate</strong></TableCell>
                    <TableCell align="center"><strong>Risk Score</strong></TableCell>
                    <TableCell align="center"><strong>Level</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.districts?.map((d: any) => (
                    <TableRow key={d.district} sx={{ bgcolor: d.risk_level === 'critical' ? '#ffebee' : d.risk_level === 'high' ? '#fff3e0' : undefined }}>
                      <TableCell>{d.district}</TableCell>
                      <TableCell>
                        <Chip label={d.zone} size="small" color={d.zone === 'Pemba' ? 'warning' : 'info'} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{d.transition_rate}%</TableCell>
                      <TableCell align="right">{d.male_transition_rate}%</TableCell>
                      <TableCell align="right">{d.female_transition_rate}%</TableCell>
                      <TableCell align="right">
                        {d.has_gender_alert && (
                          <Chip label={`${d.gender_gap}pp`} size="small" sx={{ bgcolor: '#e91e63', color: 'white', fontWeight: 600 }} />
                        )}
                        {!d.has_gender_alert && `${d.gender_gap}pp`}
                      </TableCell>
                      <TableCell align="right">{d.dropout_rate}%</TableCell>
                      <TableCell align="center">
                        <Typography fontWeight={700} color={riskColors[d.risk_level]}>
                          {d.composite_risk_score}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={d.risk_level.toUpperCase()}
                          size="small"
                          sx={{ bgcolor: riskColors[d.risk_level], color: 'white', fontWeight: 600, fontSize: '0.65rem' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Tab 1: Gender Alerts */}
      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Gender Transition Gap Alerts (threshold: {GENDER_GAP_THRESHOLD || 5}pp)
          </Typography>
          {summary.gender_alerts?.length === 0 ? (
            <Alert severity="success">No gender gap alerts above threshold.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fce4ec' }}>
                    <TableCell><strong>District</strong></TableCell>
                    <TableCell><strong>Zone</strong></TableCell>
                    <TableCell align="right"><strong>Female Rate</strong></TableCell>
                    <TableCell align="right"><strong>Male Rate</strong></TableCell>
                    <TableCell align="right"><strong>Gap</strong></TableCell>
                    <TableCell><strong>Alert</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.gender_alerts?.map((alert: any) => (
                    <TableRow key={alert.district} sx={{ bgcolor: '#fff8f8' }}>
                      <TableCell>{alert.district}</TableCell>
                      <TableCell>
                        <Chip label={alert.zone} size="small" color={alert.zone === 'Pemba' ? 'warning' : 'info'} variant="outlined" />
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#e91e63', fontWeight: 600 }}>{alert.female_rate}%</TableCell>
                      <TableCell align="right">{alert.male_rate}%</TableCell>
                      <TableCell align="right">
                        <Chip label={`${alert.gender_gap}pp`} size="small" sx={{ bgcolor: '#e91e63', color: 'white', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{alert.alert_message}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Tab 2: School Details */}
      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>School-Level Risk Details</Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by District</InputLabel>
              <Select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} label="Filter by District">
                <MenuItem value="">All Districts</MenuItem>
                {summary.districts?.map((d: any) => (
                  <MenuItem key={d.district} value={d.district}>{d.district}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {loadingSchools ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>School</strong></TableCell>
                    <TableCell><strong>District</strong></TableCell>
                    <TableCell align="right"><strong>Std VII</strong></TableCell>
                    <TableCell align="right"><strong>F1</strong></TableCell>
                    <TableCell align="right"><strong>Transition</strong></TableCell>
                    <TableCell align="right"><strong>Female Transition</strong></TableCell>
                    <TableCell align="center"><strong>Risk</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schoolData?.schools?.slice(0, 50).map((s: any) => (
                    <TableRow key={s.school_reg_number} sx={{ bgcolor: s.risk_level === 'critical' ? '#ffebee' : s.risk_level === 'high' ? '#fff3e0' : undefined }}>
                      <TableCell>{s.school_name}</TableCell>
                      <TableCell>{s.district}</TableCell>
                      <TableCell align="right">{s.std7_count}</TableCell>
                      <TableCell align="right">{s.f1_count}</TableCell>
                      <TableCell align="right">{s.transition_rate}%</TableCell>
                      <TableCell align="right">{s.female_transition_rate}%</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={s.risk_level.toUpperCase()}
                          size="small"
                          sx={{ bgcolor: riskColors[s.risk_level], color: 'white', fontWeight: 600, fontSize: '0.65rem' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {schoolData?.total_schools > 50 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing top 50 of {schoolData.total_schools} schools by risk score.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

const GENDER_GAP_THRESHOLD = 5;

export default EarlyWarningDashboard;
